import { BodyText } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { formatDate } from "date-fns";
import { memo } from "react";
import {
  type ChatMessage,
  type ChatUser,
  VIDEO_CALL_EVENT,
} from "../../types/chatMessageTypes";
import { getUserDisplayName } from "../../utils/chatSenderUtils";
interface VideoCallMessageProps {
  message: ChatMessage;
  userMap?: Record<string, ChatUser>;
  isCallPending: boolean;
  toggleVideoVisitCall: (encounterId?: string) => void;
  encounterId?: string;
}

export const VideoCallMessage = memo(
  ({ message, userMap = {} }: VideoCallMessageProps) => {
    // Get the provider name and user object
    const user = message.userId
      ? userMap[message.userId.toString()]
      : undefined;
    const providerName =
      message.practitionerName ||
      (user ? getUserDisplayName(user) : "Provider");

    // Determine message content and button based on video call event
    const getVideoCallContent = () => {
      switch (message.videoCallEvent) {
        case VIDEO_CALL_EVENT.PROVIDER_CTA:
          return null;

        case VIDEO_CALL_EVENT.PROVIDER_JOINED:
          return (
            <BodyText size="sm" textColor="primary">
              {providerName} joined video call
            </BodyText>
          );

        case VIDEO_CALL_EVENT.PATIENT_JOINED:
          return (
            <BodyText size="sm" textColor="primary">
              Patient joined video call
            </BodyText>
          );

        case VIDEO_CALL_EVENT.PROVIDER_LEFT:
          return (
            <BodyText size="sm" textColor="primary">
              {providerName} left video call
            </BodyText>
          );

        default:
          return (
            <BodyText size="sm" textColor="primary">
              Unknown video call event {message.videoCallEvent}
            </BodyText>
          );
      }
    };

    const content = getVideoCallContent();

    // Don't render anything if content is null (e.g., PROVIDER_CTA)
    if (content === null) {
      return null;
    }

    return (
      <div
        className={cn(
          "flex flex-col items-center gap-2",
          message.videoCallEvent === VIDEO_CALL_EVENT.PROVIDER_CTA &&
            "items-start",
        )}
      >
        <BodyText size="sm" textColor="primary">
          {content}
        </BodyText>
        <BodyText size="xs" textColor="tertiary">
          {formatDate(message.created, "MM/dd/yyyy h:mm a")}
        </BodyText>
      </div>
    );
  },
);
